import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { MarketplaceBreadcrumb } from '@/components/common/marketplace-breadcrumb'
import { CarCard, FilterSidebar, ListingHeader, type CarFilterState } from '@/components/marketplace'
import { EmptyState } from '@/components/feedback'
import { useMarketplaceListings } from '@/modules/cars/hooks/use-cars'
import { Loader2 } from 'lucide-react'

const initialFilters: CarFilterState = {
  brand: '',
  model: '',
  year: '',
  priceMax: '',
  fuel: '',
  transmission: '',
  bodyType: '',
  ownership: '',
  verifiedOnly: false,
}

export function AllCarsPage() {
  const [searchParams] = useSearchParams()
  const brandParam = searchParams.get('brand')
  const modelParam = searchParams.get('model')
  const userIdParam = searchParams.get('userId')

  const [searchQuery, setSearchQuery] = useState('')
  const [layout, setLayout] = useState<'grid' | 'list'>('grid')
  const [sortBy, setSortBy] = useState('featured')
  const [filters, setFilters] = useState<CarFilterState>(initialFilters)
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false)

  const { data: marketplaceData, isLoading } = useMarketplaceListings({
    search: searchQuery || undefined,
    brandSlug: brandParam || filters.brand || undefined,
    modelSlug: modelParam || filters.model || undefined,
    userId: userIdParam ? Number(userIdParam) : undefined,
    fuelType: filters.fuel || undefined,
    transmission: filters.transmission || undefined,
    maxPrice: filters.priceMax ? Number(filters.priceMax) : undefined,
    minYear: filters.year ? Number(filters.year) : undefined,
    sortBy: sortBy === 'price_asc' ? 'price_asc' : sortBy === 'price_desc' ? 'price_desc' : undefined,
  })

  const carsList = marketplaceData?.items || []

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-8 py-6">
      <MarketplaceBreadcrumb items={[{ label: 'Cars Marketplace' }]} />

      <ListingHeader
        title="Luxury Vehicles & Pre-Owned Cars"
        count={marketplaceData?.total ?? carsList.length}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        layout={layout}
        onLayoutChange={setLayout}
        sortBy={sortBy}
        onSortChange={setSortBy}
        onToggleMobileFilter={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-start">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block lg:col-span-1 sticky top-24">
          <FilterSidebar
            filters={filters}
            onFilterChange={setFilters}
            onReset={() => setFilters(initialFilters)}
          />
        </div>

        {/* Mobile Filter Drawer */}
        {isMobileFilterOpen && (
          <div className="lg:hidden col-span-1">
            <FilterSidebar
              filters={filters}
              onFilterChange={setFilters}
              onReset={() => setFilters(initialFilters)}
            />
          </div>
        )}

        {/* Cars List Grid */}
        <div className="lg:col-span-3">
          {isLoading ? (
            <div className="p-12 text-center flex flex-col items-center justify-center space-y-3">
              <Loader2 className="size-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading vehicle marketplace...</p>
            </div>
          ) : carsList.length > 0 ? (
            <div className={layout === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 gap-6' : 'space-y-6'}>
              {carsList.map((car: any) => (
                <CarCard key={car.id} car={car} layout={layout} />
              ))}
            </div>
          ) : (
            <EmptyState
              title="No Vehicles Found"
              description="No car listings match your active filters. Try adjusting your search query or reset filters."
            />
          )}
        </div>
      </div>
    </div>
  )
}
